//*new code
import React, { useState, useEffect } from 'react';
import PageContainer from 'src/components/container/PageContainer';
import DashboardCard from 'src/components/shared/DashboardCard';
import { Button, Grid, IconButton, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useMain } from '../hooks/useMain';
import TextInputs from 'src/components/inputs/TextInputs';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { ReqField } from 'src/components/CustomComponents/CustomLoader';
import { MdVisibility } from "react-icons/md";
import { MdVisibilityOff } from "react-icons/md";
import { Label } from '@mui/icons-material';
import { MdStarRate } from "react-icons/md";
import ReactSelect from 'react-select';

const activeDurationTypeOptions = ['Days', 'Months'];

const CustomTimeSlotSelector = ({ title, name, value, handleChange, required, fieldValidation, startTime = 0, minTime }) => {
  // Helper: minutes-since-midnight -> { value: "HH:MM", label: "hh:mm AM/PM" }
  const buildOption = (totalMinutes) => {
    const totalDayMinutes = 24 * 60;
    const wrapped = ((totalMinutes % totalDayMinutes) + totalDayMinutes) % totalDayMinutes;
    const hour = Math.floor(wrapped / 60);
    const minute = wrapped % 60;
    const hours12 = hour % 12 || 12;
    const period = hour < 12 ? 'AM' : 'PM';
    const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const label = `${String(hours12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
    return { value: time, label: label };
  };

  const generateTimeOptions = () => {
    const options = [];
    const totalDayMinutes = 24 * 60;

    if (minTime) {
      // Closing selector: build the list going BACKWARD from opening time.
      // First option = 1 minute before opening. Then step back 10 minutes at a
      // time (…08:59, 08:50, 08:40 … for a 09:00 opening), wrapping past
      // midnight, covering the full day without landing back on opening time.
      const [openHour, openMinute] = minTime.split(':').map(Number);
      const openTotal = openHour * 60 + openMinute;

      // First slot: exactly 1 minute before opening.
      let firstTotal = (openTotal - 1 + totalDayMinutes) % totalDayMinutes;
      options.push(buildOption(firstTotal));

      // Then step backward in 10-minute increments from the nearest 10-min
      // mark below the first slot, for the rest of the day (142 more slots,
      // stopping 1 short so we never land back on the opening time itself).
      let cur = Math.floor(firstTotal / 10) * 10;
      for (let i = 0; i < 142; i++) {
        cur = (cur - 10 + totalDayMinutes) % totalDayMinutes;
        options.push(buildOption(cur));
      }

      return options;
    }

    // Opening selector: full 24 hours in natural forward order.
    for (let total = startTime * 60; total < startTime * 60 + totalDayMinutes; total += 10) {
      options.push(buildOption(total % totalDayMinutes));
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '3.3rem',
      fontSize: '1rem',
      borderColor: state.isFocused ? '#6174DD' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 1px #6174DD' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#6174DD' : '#ccc'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '1rem'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#6174DD' : state.isFocused ? '#E0E7FF' : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: '#6174DD',
        color: 'white'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black'
    })
  };

  const formatTimeTo24hr = (selectedTime) => {
    const [time, period] = selectedTime.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleChangeSelect = (selectedOption) => {
    const formattedTime = selectedOption ? formatTimeTo24hr(selectedOption.label) : '';
    handleChange({ target: { name, value: formattedTime } });
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif' }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        component="label"
        htmlFor={name}
        sx={{ display: 'block', mb: '5px', fontSize: '0.90rem' }}
      >
        {title} {required !== false && <ReqField />}
      </Typography>
      <ReactSelect
        key={minTime || 'no-min'}
        options={timeOptions}
        value={timeOptions.find(option => option.value === value)}
        onChange={handleChangeSelect}
        isClearable
        styles={customStyles}
        maxMenuHeight={250}
        menuPlacement="auto"
      />
      {fieldValidation && (
        <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
          This field is required
        </div>
      )}
    </div>
  );
};
const initialValue = {
  branchName: '',
  branchEmail: '',
  branchPassword: '',
  confirmPassword: '',
  activeDuration: '',
  activeDurationType: 'Months',
  address: '',
  branchPhone: '', // Added branchPhone field
  salonOpeningTime: '',
  salonClosingTime: '',
};

const initialFormValidation = {
  branchNameValidation: false,
  branchEmailValidation: false,
  branchPasswordValidation: false,
  confirmPassword: false,
  activeDurationValidation: false,
};

const AddBranch = () => {
  const { createBranch, setShowMessage, ownerData } = useMain();

  const [formData, setFormData] = useState(initialValue);
  const [formValidation, setFormValidation] = useState(initialFormValidation);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({ password: false, confirmPassword: false });

  const branchNameValidation = !formData.branchName.trim();
  const branchEmailValidation = !formData.branchEmail.trim();
  const branchPasswordValidation = !formData.branchPassword.trim();
  const confirmPasswordValidation = !formData.confirmPassword.trim();
  const activeDurationValidation =
    !formData.activeDuration || !(Number(formData.activeDuration) > 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleActiveDurationTypeChange = (event, newActiveDurationType) => {
    if (newActiveDurationType !== null) {
      setFormData({ ...formData, activeDurationType: newActiveDurationType });
    }
  };
  const handleTogglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };


  const handleSubmit = async () => {
    const {
      branchName,
      branchEmail,
      branchPassword,
      activeDuration,
      confirmPassword,
      branchPhone,
    } = formData;

    const validation = {
      branchNameValidation: !branchName.trim(),
      branchEmailValidation: !branchEmail.trim(),
      branchPasswordValidation: !branchPassword.trim(),
      confirmPasswordValidation: !confirmPassword.trim(),
      activeDurationValidation: !activeDuration || !(Number(activeDuration) > 0),
    };

    setFormValidation((pre) => ({ ...pre, ...validation }));

    if (Object.values(validation).some((item) => item)) return;

    setLoading(true);

    try {
      const res = await createBranch({
        ...formData,
        ownerId: ownerData._id,
      });
      if (res.statusCode === 200) {
        setShowMessage({
          message: res.message || 'Successfully created branch',
          messageType: 'success',
        });
        setFormData(initialValue);
        setFormValidation(initialFormValidation);
      } else {
        setShowMessage({ message: res.message || "Can't create branch", messageType: 'error' });
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Error Occurs creating branch',
        messageType: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
// Returns the list of valid closing-time values (10-min slots) given an opening time,
// wrapping past midnight — mirrors CustomTimeSlotSelector's generateTimeOptions logic.
const getValidClosingTimes = (openingTime) => {
  if (!openingTime) return [];
  const [h, m] = openingTime.split(':').map(Number);
  const totalSlots = 24 * 6;
  const startIndex = h * 6 + Math.floor(m / 10);
  const valid = [];
  for (let i = 1; i <= totalSlots; i++) {
    const slotIndex = (startIndex + i) % totalSlots;
    const hour = Math.floor(slotIndex / 6);
    const minute = (slotIndex % 6) * 10;
    valid.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  }
  return valid;
};

useEffect(() => {
  setFormData((prev) => {
    if (!prev.salonOpeningTime || !prev.salonClosingTime) return prev;

    const validClosingTimes = getValidClosingTimes(prev.salonOpeningTime);
    if (!validClosingTimes.includes(prev.salonClosingTime)) {
      return { ...prev, salonClosingTime: '' };
    }
    return prev;
  });
}, [formData.salonOpeningTime]);
  // This logic disables the mouse wheel event
  useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };

    window.addEventListener('wheel', handleWheel);
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);

  console.log(formData, 'data');

  return (
    <PageContainer title="Add Branch" description="This is Add New Branch">
      <DashboardCard title="Add New Branch">
        <Box style={{ marginTop: '20px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextInputs
                title={'Branch Name'}
                name={'branchName'}
                value={formData?.branchName}
                handleChange={handleChange}
                type={'text'}
                placeholder={'Branch Name'}
                fieldValidation={branchNameValidation && formValidation.branchNameValidation}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputs
                title={'Branch Email'}
                name={'branchEmail'}
                value={formData?.branchEmail}
                handleChange={handleChange}
                type={'email'}
                placeholder={'Email'}
                fieldValidation={branchEmailValidation && formValidation.branchEmailValidation}
              />
            </Grid>
          </Grid>
          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* <TextInputs
                  title={'Branch Password'}
                  name={'branchPassword'}
                  placeholder={'********'}
                  value={formData?.branchPassword}
                  handleChange={handleChange}
                  type={'password'}
                  fieldValidation={
                    branchPasswordValidation && formValidation.branchPasswordValidation
                  }
                /> */}
                <label>Branch Password <MdStarRate style={{color:'red',fontSize:'0.48rem',marginBottom:'0.5rem'}}/></label>
                <TextField
                  // label="Branch Password"
                  name="branchPassword"
                  value={formData.branchPassword}
                  onChange={handleChange}
                  type={showPassword.password ? 'text' : 'password'}
                  placeholder="********"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePasswordVisibility('password')}
                          edge="end"
                        >
                          {showPassword.password ? <MdVisibilityOff /> : <MdVisibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={branchPasswordValidation && formValidation.branchPasswordValidation}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {/* <TextInputs
                  title={'Confirm Password'}
                  name={'confirmPassword'}
                  placeholder={'********'}
                  value={formData?.confirmPassword}
                  handleChange={handleChange}
                  type={'password'}
                  fieldValidation={
                    confirmPasswordValidation && formValidation.confirmPasswordValidation
                  }
                /> */}
                <label>Confirm Password<MdStarRate style={{color:'red',fontSize:'0.48rem',marginBottom:'0.5rem'}}/></label>
                <TextField
                  // label="Confirm Password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  placeholder="********"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePasswordVisibility('confirmPassword')}
                          edge="end"
                        >
                          {showPassword.confirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={confirmPasswordValidation && formValidation.confirmPasswordValidation}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextInputs
                  title={'Phone Number'}
                  name={'branchPhone'}
                  value={formData?.branchPhone}
                  handleChange={handleChange}
                  type={'number'}
                  placeholder={'87******22'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInputs
                  title={'Address'}
                  name={'address'}
                  value={formData?.address}
                  handleChange={handleChange}
                  type={'text'}
                  placeholder={'Address'}
                  required={true}
                />
              </Grid>
            </Grid>
          </Box>
          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={6}>
                <CustomTimeSlotSelector
                  title="Salon Opening Time"
                  name="salonOpeningTime"
                  value={formData.salonOpeningTime}
                  handleChange={handleChange}
                  startTime={0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <CustomTimeSlotSelector
                  title="Salon Closing Time"
                  name="salonClosingTime"
                  value={formData.salonClosingTime}
                  handleChange={handleChange}
                  startTime={0}
                  minTime={formData.salonOpeningTime}
                />
              </Grid>
            </Grid>
          </Box>
          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextInputs
                      title={'Active Duration'}
                      name={'activeDuration'}
                      value={formData?.activeDuration}
                      handleChange={handleChange}
                      type={'number'}
                      min={'0'}
                      placeholder={'0'}
                      fieldValidation={
                        activeDurationValidation && formValidation.activeDurationValidation
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <div
                      style={{
                        marginTop: '2px',
                        marginBottom: '5px',
                        fontWeight: 'bold',
                        color: 'black',
                      }}
                    >
                      Active Duration Type <ReqField />
                    </div>
                    <ToggleButtonGroup
                      value={formData.activeDurationType}
                      exclusive
                      onChange={handleActiveDurationTypeChange}
                      aria-label="active duration type"
                      fullWidth
                      sx={{
                        borderRadius: '4px',
                        // borderRadius: '50%',
                        // height: '30px',
                        backgroundColor: '#f0f0f0',
                        width: '150px',
                      }}
                    >
                      {activeDurationTypeOptions.map((type) => (
                        <ToggleButton
                          key={type}
                          value={type}
                          aria-label={type}
                          sx={{
                            color: '#000',
                            backgroundColor: '#e0e0e0',
                            // borderRadius:'50%',

                            height: '3.3rem',
                            // width:'4rem',
                            width: '150px',
                            '&.Mui-selected': {
                              backgroundColor: '#6174DD',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: '#6174DD',
                              },
                            },
                            '&:not(.Mui-selected)': {
                              '&:hover': {
                                backgroundColor: '#d0d0d0',
                              },
                            },
                          }}
                        >
                          {type}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <div style={{ width: '200px', margin: '3rem auto' }}>
          {loading ? (
            <ShowLoader />
          ) : (
            <Button
              variant="contained"
              fullWidth
              color="primary"
              sx={{
                background: '#6174DD',
              }}
              onClick={handleSubmit}
            >
              Create Branch
            </Button>
          )}
        </div>
      </DashboardCard>
    </PageContainer>
  );
};

export default AddBranch;
